import type {
  SportsDataProvider, ProviderFixture, ProviderLineup, ProviderHeadToHead,
  ProviderH2HMatch, ProviderTeamStats, ProviderPlayerStats,
  ProviderMatchDetails, ProviderInjury, ProviderSuspension, ProviderCard,
  ProviderStandings,
  ProviderFixturePlayers,
} from "./types";
import { listMatchRosters, getMatchRoster } from "@/lib/sports/matchRosters";

function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function buildH2HMatches(homeId: string, awayId: string): ProviderH2HMatch[] {
  const rng = seeded(homeId.charCodeAt(0) * 100 + awayId.charCodeAt(0));
  const competitions = ["FIFA WC Qualifier", "Friendly", "Copa America", "FIFA WC", "Confederations Cup"];
  const out: ProviderH2HMatch[] = [];
  const total = 8 + Math.floor(rng() * 4);
  for (let i = 0; i < total; i++) {
    const hg = Math.floor(rng() * 4);
    const ag = Math.floor(rng() * 4);
    const winner: "home" | "away" | "draw" = hg === ag ? "draw" : hg > ag ? "home" : "away";
    const d = new Date();
    d.setMonth(d.getMonth() - (i + 1) * 6);
    out.push({
      date: d.toISOString(),
      competition: competitions[Math.floor(rng() * competitions.length)],
      homeTeamId: homeId, awayTeamId: awayId,
      homeTeam: homeId, awayTeam: awayId,
      homeGoals: hg, awayGoals: ag, winner,
    });
  }
  return out;
}

export const DemoProvider: SportsDataProvider = {
  name: "demo",
  async getFixtures() {
    return listMatchRosters().map<ProviderFixture>((r) => ({
      id: r.matchId,
      homeTeamId: r.homeTeam, awayTeamId: r.awayTeam,
      homeTeam: r.homeTeam, awayTeam: r.awayTeam,
      competitionId: "fifa-wc-2026",
      kickoff: new Date().toISOString(),
      status: "LIVE",
    }));
  },
  async getLineups(fixtureId: string) {
    // Real Data Phase 3 — the Lineups module renders OFFICIAL data only.
    // Demo/simulated lineups would violate `dataPolicy.ts`, so this
    // endpoint returns an empty payload and the UI shows the lifecycle
    // placeholder ("Official lineups unavailable"). The community
    // simulation catalog (matchRosters.ts) is still consumed elsewhere.
    return {
      fixtureId,
      official: false,
      provider: "demo",
      home: null,
      away: null,
      homePlayers: [],
      awayPlayers: [],
    };
  },
  async getHeadToHead(homeTeamId, awayTeamId, competitionId = "fifa-wc-2026") {
    return {
      homeTeamId, awayTeamId, competitionId,
      matches: buildH2HMatches(homeTeamId, awayTeamId),
    };
  },
  async getTeamStats(teamId): Promise<ProviderTeamStats> {
    const rng = seeded(teamId.charCodeAt(0) * 31);
    const played = 10 + Math.floor(rng() * 10);
    const wins = Math.floor(played * (0.4 + rng() * 0.3));
    const draws = Math.floor((played - wins) * (0.4 + rng() * 0.3));
    return {
      teamId, played, wins, draws,
      losses: played - wins - draws,
      goalsFor: Math.floor(played * (1 + rng() * 1.5)),
      goalsAgainst: Math.floor(played * (0.5 + rng() * 1)),
    };
  },
  async getPlayerStats(playerId): Promise<ProviderPlayerStats> {
    const rng = seeded(playerId * 17 + 1);
    return {
      playerId, name: `Player #${playerId}`,
      goals: Math.floor(rng() * 12),
      assists: Math.floor(rng() * 10),
      minutes: 600 + Math.floor(rng() * 1200),
    };
  },
  // Real Data Phase 4 — DemoProvider MUST NOT emit fabricated per-fixture
  // player statistics. Returning an empty payload forces the UI to show the
  // lifecycle-appropriate placeholder from `dataPolicy.ts`.
  async getFixturePlayers(fixtureId: string): Promise<ProviderFixturePlayers> {
    return {
      fixtureId,
      official: false,
      provider: "demo",
      home: null,
      away: null,
    };
  },
  async getMatchDetails(fixtureId): Promise<ProviderMatchDetails> {
    // Real Data Phase 5 — DemoProvider MUST NOT fabricate official Match
    // Header fields (score, minute, status, kickoff). Return a
    // non-official empty payload so the UI renders the lifecycle
    // placeholder from dataPolicy.ts.
    const r = getMatchRoster(fixtureId);
    return {
      fixtureId,
      homeTeam: r.homeTeam,
      awayTeam: r.awayTeam,
      competition: "FIFA World Cup 2026",
      status: "SCHEDULED",
      minute: undefined,
      scoreHome: undefined,
      scoreAway: undefined,
      venue: undefined,
      statusShort: null,
      statusLong: null,
      extraMinute: null,
      penaltyHome: null,
      penaltyAway: null,
      kickoff: null,
      homeTeamId: null,
      awayTeamId: null,
      homeLogo: null,
      awayLogo: null,
      competitionLogo: null,
      round: null,
      referee: null,
      official: false,
      provider: "demo",
    };
  },
  async getInjuries(teamId): Promise<ProviderInjury[]> {
    const rng = seeded(teamId.charCodeAt(0) * 13);
    const n = Math.floor(rng() * 3);
    return Array.from({ length: n }).map((_, i) => ({
      playerId: 100 + i,
      playerName: `${teamId} Player ${i + 1}`,
      team: teamId,
      reason: ["Hamstring", "Knee", "Ankle"][i % 3],
      expectedReturn: "2 weeks",
    }));
  },
  async getSuspensions(teamId): Promise<ProviderSuspension[]> {
    const rng = seeded(teamId.charCodeAt(0) * 19);
    const n = Math.floor(rng() * 2);
    return Array.from({ length: n }).map((_, i) => ({
      playerId: 200 + i,
      playerName: `${teamId} Susp ${i + 1}`,
      team: teamId,
      reason: "Yellow accumulation",
      matchesRemaining: 1,
    }));
  },
  async getCards(fixtureId): Promise<ProviderCard[]> {
    const rng = seeded(fixtureId.length * 23);
    const n = Math.floor(rng() * 5);
    return Array.from({ length: n }).map((_, i) => ({
      playerId: 300 + i,
      playerName: `Player ${i + 1}`,
      team: i % 2 === 0 ? "HOME" : "AWAY",
      type: rng() > 0.85 ? "red" : "yellow",
      minute: Math.floor(rng() * 90),
    }));
  },
  async getStats(fixtureId): Promise<Record<string, number | string>> {
    const rng = seeded(fixtureId.length * 29);
    return {
      possessionHome: 40 + Math.floor(rng() * 20),
      possessionAway: 40 + Math.floor(rng() * 20),
      shotsHome: Math.floor(rng() * 15),
      shotsAway: Math.floor(rng() * 15),
      xgHome: Number((rng() * 2.5).toFixed(2)),
      xgAway: Number((rng() * 2.5).toFixed(2)),
      cornersHome: Math.floor(rng() * 8),
      cornersAway: Math.floor(rng() * 8),
    };
  },
  async getStandings(competitionId): Promise<ProviderStandings> {
    const teams = ["MEX", "ARG", "BRA", "USA", "ESP", "FRA"];
    const rng = seeded(competitionId.length * 31);
    const rows = teams.map((t, i) => {
      const wins = Math.floor(rng() * 5);
      const draws = Math.floor(rng() * 3);
      const losses = Math.floor(rng() * 3);
      const played = wins + draws + losses;
      return {
        rank: i + 1, teamId: t, teamName: t, played,
        wins, draws, losses,
        points: wins * 3 + draws,
        goalsFor: Math.floor(rng() * 12),
        goalsAgainst: Math.floor(rng() * 8),
      };
    }).sort((a, b) => b.points - a.points).map((r, i) => ({ ...r, rank: i + 1 }));
    return { competitionId, rows };
  },
  // Demo provider never fabricates official football events (dataPolicy.ts).
  async getEvents(_fixtureId): Promise<import("./types").ProviderMatchEvent[]> {
    return [];
  },
};